import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close,
  FileDownload,
  DeleteOutline,
} from "@mui/icons-material";
import { DateRangeFilter } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useCreateTradeDataExportMutation, useDeleteReportsMutation, useGetAllReportsQuery, useLazyGetReportDownloadUrlQuery } from "@/services";
import { tokens } from "@/tokens";

interface Props {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const TradeDataExportModal: FC<Props> = ({ show, setShow }) => {
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const [dateRange, setDateRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
  });
  const [_, setPendingReportId] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const reportQueryVars = useMemo(() => {
    const base: any = { pageNo: 1, pageSize: 20 };
    if (user?.userRole && [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)) {
      base.clientId = selectedClient?._id;
    }
    if (user?.userRole && [roles.TDADMIN].includes(user.userRole)) {
      base.tenantId = selectedTenant?._id;
    }
    return base;
  }, [user, selectedClient?._id, selectedTenant?._id]);

  const { data: reportsData, refetch } = useGetAllReportsQuery({ ...reportQueryVars, onlyExports: true }, { skip: !user });
  const tradeExports = useMemo(() => (reportsData?.data || []).filter((r) => r.reportType === "TRADE_DATA_EXPORT"), [reportsData]);

  const [createExport, { isLoading: isCreating }] = useCreateTradeDataExportMutation();
  const [deleteReport] = useDeleteReportsMutation();
  const [getReportDownloadUrl] = useLazyGetReportDownloadUrlQuery();

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleCreate = async () => {
    if (!user) return;
    const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);
    const input: any = {
      dateFrom: dateRange.startDate ? toIsoDate(dateRange.startDate) : "1970-01-01",
      dateTo: dateRange.endDate ? toIsoDate(dateRange.endDate) : toIsoDate(new Date()),
    };
    if (user.userRole && [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)) {
      input.clientId = selectedClient?._id;
    }
    if (user.userRole && [roles.TDADMIN].includes(user.userRole)) {
      input.tenantId = selectedTenant?._id;
    }
    try {
      const res = await createExport(input).unwrap();
      const newReportId = res?.data;
      if (newReportId) {
        setPendingReportId(newReportId);
        // start polling every 4s up to ~2 minutes
        const start = Date.now();
        pollRef.current = window.setInterval(async () => {
          const result = await refetch();
          const latest = (result.data?.data || []) as IReport[];
          const target = latest.find((r) => r._id === newReportId);
          const done = !!target?.s3Key;
          const timedOut = Date.now() - start > 120000; // 2 minutes
          if (done || timedOut) {
            stopPolling();
            setPendingReportId(null);
          }
        }, 4000);
      } else {
        await refetch();
      }
    } catch {}
  };

  const handleDownload = async (report: IReport) => {
    const url = await getReportDownloadUrl({ reportId: report._id }).unwrap();
    const a = document.createElement("a");
    a.href = url;
    a.download = "trade-data-export.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (report: IReport) => {
    await deleteReport(report._id).unwrap();
    await refetch();
  };

  return (
    <Dialog
      open={show}
      onClose={() => setShow(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
      }}>
        <Box>
          <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: "15px", color: tokens.text.primary }}>
            Export Trade Data
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
            Create exports and download from your export history
          </Typography>
        </Box>
        <IconButton onClick={() => setShow(false)} size="small" sx={{ color: tokens.text.secondary }}>
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Stack gap={2.5}>
          {/* Previous Exports */}
          <Box>
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, color: tokens.text.primary, mb: 1.5 }}>
              Previous Exports
            </Typography>
            <Box sx={{
              maxHeight: 260,
              overflowY: "auto",
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              bgcolor: tokens.bg.surface,
            }}>
              {(tradeExports || []).length === 0 && (
                <Typography sx={{ p: 2.5, color: tokens.text.muted, fontSize: tokens.fontSize.md, textAlign: "center" }}>
                  No previous exports
                </Typography>
              )}
              {(tradeExports || []).map((r, index) => (
                <Stack
                  key={r._id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: index < tradeExports.length - 1 ? `1px solid ${tokens.border.light}` : "none",
                    "&:hover": { bgcolor: tokens.bg.hover },
                  }}
                >
                  <Stack>
                    <Typography sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, color: tokens.text.primary }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                      {r.exportDateAbbrev || ""}
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={0.75}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownload sx={{ fontSize: 16 }} />}
                      disabled={!r.s3Key}
                      onClick={() => handleDownload(r)}
                      sx={{
                        borderColor: tokens.border.default,
                        color: tokens.brand.primary,
                        borderRadius: tokens.radius.md,
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.base,
                        height: 32,
                        "&:hover": { borderColor: tokens.brand.primary, bgcolor: "rgba(11,73,75,0.04)" },
                        "&.Mui-disabled": { borderColor: tokens.border.light, color: tokens.text.disabled },
                      }}
                    >
                      Download
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(r)}
                      sx={{
                        color: tokens.text.muted,
                        "&:hover": { color: tokens.status.danger, bgcolor: "rgba(239,68,68,0.06)" },
                      }}
                    >
                      <DeleteOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
            </Box>
          </Box>

          {/* Create New Export */}
          <Box>
            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, color: tokens.text.primary, mb: 1.5 }}>
              Create New Export
            </Typography>
            <DateRangeFilter
              value={{ startDate: dateRange.startDate, endDate: dateRange.endDate }}
              onSelectDate={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
            />
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
      }}>
        <Button
          onClick={() => setShow(false)}
          variant="outlined"
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          disabled={isCreating}
          variant="contained"
          startIcon={isCreating ? <CircularProgress size={16} sx={{ color: tokens.bg.surface }} /> : undefined}
          sx={{
            bgcolor: tokens.brand.primary,
            color: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            boxShadow: "none",
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: tokens.text.disabled, color: tokens.bg.surface },
          }}
        >
          Create Export
        </Button>
      </Box>
    </Dialog>
  );
};

export default TradeDataExportModal;
